/* eslint-disable react/jsx-no-literals -- pre-i18n migration; replace literals with t() and remove this directive (see docs/i18n-migration-plan.md) */
/**
 * AdminSearchBar — cross-module search for the admin header.
 * Searches Users, Vendors, Venues, Categories, Bookings, Blog posts and
 * Audit logs via /admin/search, and routes each result to its admin page.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import searchService from '@services/searchService';

// type → { icon, label, route(item) }
const TYPE_META = {
  user:     { icon: 'bi-person-lines-fill', label: 'Users',      route: (it) => `/admin/users/${it.id}` },
  vendor:   { icon: 'bi-shop',              label: 'Vendors',    route: () => '/admin/vendors' },
  venue:    { icon: 'bi-building',          label: 'Venues',     route: () => '/admin/venues' },
  category: { icon: 'bi-tags',              label: 'Categories', route: () => '/admin/categories' },
  booking:  { icon: 'bi-calendar-check',    label: 'Bookings',   route: () => '/admin/bookings' },
  blog:     { icon: 'bi-journal-richtext',  label: 'Blog Posts', route: () => '/admin/blog' },
  audit:    { icon: 'bi-journal-text',      label: 'Audit Log',  route: () => '/admin/audit' },
};
const TYPE_ORDER = ['user', 'vendor', 'venue', 'category', 'booking', 'blog', 'audit'];

const AdminSearchBar = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); inputRef.current?.focus(); setOpen(true); }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await searchService.adminSearch(q.trim());
      setResults(res.data?.data?.results || []);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 250);
    if (val.trim().length >= 2) setOpen(true);
  };

  const goTo = (item) => {
    const meta = TYPE_META[item.type];
    if (meta) navigate(meta.route(item));
    setOpen(false);
    setQuery('');
    setResults([]);
  };

  // Group flat results by type, preserving a sensible section order.
  const grouped = TYPE_ORDER
    .map(type => ({ type, items: results.filter(r => r.type === type) }))
    .filter(g => g.items.length > 0);

  return (
    <div ref={ref} style={{ position: 'relative', width: 280 }}>
      <div style={{ position: 'relative' }}>
        <i className="bi bi-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: '0.85rem' }} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => query.trim().length >= 2 && setOpen(true)}
          placeholder={(navigator.userAgent?.includes('Mac') ? '⌘K ' : 'Ctrl+K ') + 'Search admin...'}
          style={{
            width: '100%', padding: '8px 12px 8px 36px', border: '1px solid #E5E7EB',
            borderRadius: 24, fontSize: '0.82rem', outline: 'none', background: '#F9FAFB', transition: 'all 0.2s',
            ...(open ? { borderColor: '#9B30FF', boxShadow: '0 0 0 3px rgba(155,48,255,0.1)', background: '#fff' } : {}),
          }}
        />
        {loading && <i className="bi bi-arrow-repeat" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', animation: 'spin 1s linear infinite', color: '#9B30FF', fontSize: '0.8rem' }} />}
      </div>

      {open && query.trim().length >= 2 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, minWidth: 340,
          background: '#fff', borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
          zIndex: 9999, maxHeight: 460, overflowY: 'auto', border: '1px solid #F3F4F6',
        }}>
          {grouped.length === 0 ? (
            <div className="text-center text-muted py-3" style={{ fontSize: '0.8rem' }}>
              {loading ? 'Searching…' : `No matches for "${query.trim()}"`}
            </div>
          ) : grouped.map(({ type, items }) => {
            const meta = TYPE_META[type];
            return (
              <div key={type} style={{ padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
                <div className="px-3 pb-1 d-flex align-items-center gap-1" style={{ fontSize: '0.62rem', color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                  <i className={`bi ${meta.icon}`} style={{ fontSize: '0.62rem' }} />{meta.label} ({items.length})
                </div>
                {items.map(item => (
                  <div key={`${type}-${item.id}`} onClick={() => goTo(item)}
                    className="d-flex align-items-center gap-2 px-3 py-2"
                    style={{ cursor: 'pointer', fontSize: '0.82rem', transition: 'background 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#F8F4FF'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    <i className={`bi ${meta.icon}`} style={{ color: '#9B30FF', fontSize: '0.9rem', minWidth: 20 }} />
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="fw-medium" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</div>
                      {item.sub && <small className="text-muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{item.sub}</small>}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
      <style>{`@keyframes spin { to { transform: translateY(-50%) rotate(360deg); } }`}</style>
    </div>
  );
};

export default AdminSearchBar;
